import { Doc } from '@dadohr/shared/domainTypes/document';
import { IExperience } from '@dadohr/shared/domainTypes/experience';
import { refreshTimestamp } from '../firebase';

export type DerivedTimingOverride = {
  type: 'TASK' | 'MSG' | 'AUTOMATION';
  id: string;
  start?: number;
  end?: number;
  at?: number;
};

/**
 * Derive per-item timing overrides from a manual-enroll staged instance.
 *
 * The manual-enroll dialog's "Start today" / "Send today" / "Run today" actions
 * (`moveTaskToToday` / `moveMessageToToday` / `moveAutomationToToday`) move an
 * item off its template-derived date and set `timing.manualOverride = true` on
 * the local instance preview.
 *
 * In v1, `enrollUsers` persisted the staged instance directly (`batchSetDocs`),
 * so those moved timings landed in Firestore as-is. In v2, `enrollUsers` routes
 * through the `experiences_enrollParticipant` callable, which rebuilds the
 * instance from the TEMPLATE — so the moves were silently discarded. We lift the
 * overridden items into an array the BE builder applies
 * (`functions/experiences/src/public/index.ts:createExperienceInstance`), exactly
 * as we already do for skips (`derivePreSkipsFromStagedInstance`).
 *
 * Contract:
 * - ids are TEMPLATE ids: `task.templateId`, the `messagesStatuses` key (which IS
 *   the template id), the `automations` key (template id). The BE builder matches
 *   on template ids since it generates fresh instance ids at enrollment.
 * - start/end/at are MS EPOCH. We pass numbers (not Timestamps) over the callable
 *   boundary; the onCall rebuilds real Timestamps via `Timestamp.fromMillis`.
 *   `refreshTimestamp` guards against raw `{_seconds,_nanoseconds}` proto objects
 *   before `.toMillis()`.
 */
export const deriveTimingOverridesFromStagedInstance = (
  d: Doc<IExperience>
): DerivedTimingOverride[] => {
  const out: DerivedTimingOverride[] = [];
  const ms = (t: unknown): number | undefined => refreshTimestamp(t)?.toMillis();

  d.data.tasks?.forEach((t) => {
    if (t.timing?.manualOverride && t.templateId) {
      out.push({
        type: 'TASK',
        id: t.templateId,
        start: ms(t.timing.start),
        end: ms(t.timing.end)
      });
    }
  });

  Object.entries(d.data.messagesStatuses || {}).forEach(([msgId, ms_]) => {
    if (ms_.timing?.manualOverride) {
      out.push({ type: 'MSG', id: msgId, at: ms(ms_.timing.at) });
    }
  });

  Object.entries(d.data.automations || {}).forEach(([autId, aut]) => {
    if (aut.timing?.manualOverride) {
      out.push({ type: 'AUTOMATION', id: autId, at: ms(aut.timing.at) });
    }
  });

  return out;
};
